import type { CSSProperties } from 'react';

const MOSAIC_CDN_URL =
  'https://static-cdn.usertesting.com/design-system-static-assets-public/toolkit-svg-assets/v6.4.0/v2/userinterview';

type MosaicIllustrations = {
  general:
    | 'cat'
    | 'customer-feedback'
    | 'information-overload'
    | 'multitasking'
    | 'review-wireframe'
    | 'signpost'
    | 'user-interviews';
  'manual-application': 'invited' | 'unavailable-data';
  participant:
    | 'application-success'
    | 'best-studies'
    | 'certified'
    | 'contact-info'
    | 'device-info'
    | 'gift'
    | 'hello'
    | 'no-studies'
    | 'paid-diary-studies'
    | 'paid-focus-groups'
    | 'paid-in-person'
    | 'paid-interviews'
    | 'paid-online-studies'
    | 'paid-surveys'
    | 'phone-studies'
    | 'post_onboarding'
    | 'professional-details'
    | 'profile-banner'
    | 'recruit-participants'
    | 'referral-reward'
    | 'sign-in'
    | 'start-applying'
    | 'verify-phone'
    | 'video-upload'
    | 'welcome';
  process: 'success';
  researcher:
    | 'add-participants'
    | 'highlight-transcript'
    | 'new-consumption-research'
    | 'no-participants'
    | 'no-reels'
    | 'no-results'
    | 'recruit-target'
    | 'scheduled-participants'
    | 'scheduled-participants-2'
    | 'sign-in';
  test: 'success';
  'test-launch': 'journey';
};

export type MosaicIllustrationCategory = keyof MosaicIllustrations;

export type MosaicIllustrationName<C extends MosaicIllustrationCategory> =
  MosaicIllustrations[C];

export function mosaicIllustrationUrl<C extends MosaicIllustrationCategory>(
  category: C,
  name: MosaicIllustrationName<C>,
  { dark = false }: { dark?: boolean } = {},
) {
  return `${MOSAIC_CDN_URL}${dark ? '/dark' : ''}/illustrations/${category}/${name}.svg`;
}

// Mosaic SVGs pad their art; scaling it up fills the img footprint without changing layout
function mosaicIllustrationZoomStyle(zoom: number): CSSProperties {
  return { pointerEvents: 'none', transform: `scale(${zoom})` };
}

type MosaicPictograms = {
  ai: 'cursor-sparkle' | 'robot' | 'star-left';
  communication: 'chat' | 'email-notification' | 'feedback' | 'send-1';
  core: 'check' | 'lock' | 'search-1';
  data: 'flow-diagram' | 'right-fit';
  device: 'monitor-cursor';
  document: 'recruit-between';
  finance: 'earn' | 'key-sparkle' | 'money-bag' | 'verify-badge';
  'hand-gesture': 'handshake-default';
  hobby: 'plant-pot' | 'scuba-diving' | 'tree-test' | 'video';
  location: 'target-hit';
  medal: 'star';
  people: 'verify-person';
  schedule: 'calendar-default' | 'clock-default-1';
};

export type MosaicPictogramCategory = keyof MosaicPictograms;

export type MosaicPictogramName<C extends MosaicPictogramCategory> =
  MosaicPictograms[C];

export function mosaicPictogramUrl<C extends MosaicPictogramCategory>(
  category: C,
  name: MosaicPictogramName<C>,
  { dark = false }: { dark?: boolean } = {},
) {
  return `${MOSAIC_CDN_URL}${dark ? '/dark' : ''}/decorative-pictograms/${category}/${name}.svg`;
}

type MosaicIllustrationOptions = {
  dark?: boolean;
  maxHeight?: number;
  maxWidth?: number;
  zoom?: number;
};

export function mosaicIllustration<C extends MosaicIllustrationCategory>(
  category: C,
  name: MosaicIllustrationName<C>,
  { dark, maxHeight, maxWidth, zoom }: MosaicIllustrationOptions = {},
): { src: string; style?: CSSProperties } {
  // The SVGs are 2000×2000, so a cap is needed wherever the art relied on its natural size
  const style: CSSProperties = {
    maxHeight,
    maxWidth,
    ...(zoom && mosaicIllustrationZoomStyle(zoom)),
  };

  return {
    src: mosaicIllustrationUrl(category, name, { dark }),
    style: maxHeight || maxWidth || zoom ? style : undefined,
  };
}

export function mosaicPictogram<C extends MosaicPictogramCategory>(
  category: C,
  name: MosaicPictogramName<C>,
  { dark, size }: { dark?: boolean; size?: number } = {},
): { src: string; style?: CSSProperties } {
  // The SVGs are 24×24, so a size is needed wherever the art relied on its natural size
  return {
    src: mosaicPictogramUrl(category, name, { dark }),
    style: size
      ? { height: size, objectFit: 'contain', width: size }
      : undefined,
  };
}
